import { Fragment, useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useToast } from "@/components/ToastProvider";
import type { MasterContactRegistryEntry, MasterContactRegistryUpsert } from "@/hooks/useMasterControl";

const EMPTY_FORM: MasterContactRegistryUpsert = {
  venue_name: "",
  domain: "",
  location_city: "",
  location_region: "",
  location_country: "",
  booking_email: "",
  contact_url: "",
  source_url: "",
  notes: "",
};

function normalizeForm(form: MasterContactRegistryUpsert): MasterContactRegistryUpsert {
  return {
    ...form,
    venue_name: form.venue_name?.trim() ?? "",
    domain: form.domain?.trim() || null,
    location_city: form.location_city?.trim() || null,
    location_region: form.location_region?.trim() || null,
    location_country: form.location_country?.trim() || null,
    booking_email: form.booking_email?.trim() || null,
    contact_url: form.contact_url?.trim() || null,
    source_url: form.source_url?.trim() || null,
    notes: form.notes?.trim() || null,
  };
}

type MCMasterContactRegistryProps = {
  entries: MasterContactRegistryEntry[];
  fetchEntries: (query?: string) => Promise<void>;
  upsertEntry: (payload: MasterContactRegistryUpsert) => Promise<MasterContactRegistryEntry | null>;
  updateEntry: (id: string, payload: MasterContactRegistryUpsert) => Promise<MasterContactRegistryEntry | null>;
  deleteEntry: (id: string) => Promise<void>;
};

export default function MCMasterContactRegistry({
  entries,
  fetchEntries,
  upsertEntry,
  updateEntry,
  deleteEntry,
}: MCMasterContactRegistryProps) {
  const { push } = useToast();
  const [query, setQuery] = useState("");
  const [form, setForm] = useState<MasterContactRegistryUpsert>(EMPTY_FORM);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [inlineEditId, setInlineEditId] = useState<string | null>(null);
  const [inlineDraft, setInlineDraft] = useState<MasterContactRegistryUpsert | null>(null);
  const [inlineSaving, setInlineSaving] = useState(false);

  useEffect(() => {
    fetchEntries().catch(() => {
      push({ title: "Registry load failed", message: "Unable to fetch master contact registry." });
    });
  }, [fetchEntries, push]);

  const handleSubmit = async () => {
    if (!form.venue_name.trim()) {
      push({ title: "Venue name required", message: "Please provide a venue name." });
      return;
    }
    setSubmitting(true);
    try {
      const payload = normalizeForm(form);
      if (editingId) {
        await updateEntry(editingId, payload);
        push({ title: "Registry updated", message: "Entry updated successfully." });
      } else {
        await upsertEntry(payload);
        push({ title: "Registry entry created", message: "Entry added to master registry." });
      }
      setForm(EMPTY_FORM);
      setEditingId(null);
    } catch (err) {
      push({ title: "Registry save failed", message: "Unable to save registry entry." });
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (entry: MasterContactRegistryEntry) => {
    setEditingId(entry.id);
    setForm({
      venue_name: entry.venue_name ?? "",
      domain: entry.domain ?? "",
      location_city: entry.location_city ?? "",
      location_region: entry.location_region ?? "",
      location_country: entry.location_country ?? "",
      booking_email: entry.booking_email ?? "",
      contact_url: entry.contact_url ?? "",
      source_url: entry.source_url ?? "",
      notes: entry.notes ?? "",
      confidence_score: entry.confidence_score ?? undefined,
      confidence_reasons: entry.confidence_reasons ?? [],
      tags: entry.tags ?? [],
      last_verified_at: entry.last_verified_at ?? undefined,
    });
  };

  const startInlineEdit = (entry: MasterContactRegistryEntry) => {
    setInlineEditId(entry.id);
    setInlineDraft({
      venue_name: entry.venue_name ?? "",
      domain: entry.domain ?? "",
      location_city: entry.location_city ?? "",
      location_region: entry.location_region ?? "",
      location_country: entry.location_country ?? "",
      booking_email: entry.booking_email ?? "",
      contact_url: entry.contact_url ?? "",
      source_url: entry.source_url ?? "",
      notes: entry.notes ?? "",
      confidence_score: entry.confidence_score ?? undefined,
      confidence_reasons: entry.confidence_reasons ?? [],
      tags: entry.tags ?? [],
      last_verified_at: entry.last_verified_at ?? undefined,
    });
  };

  const cancelInlineEdit = () => {
    setInlineEditId(null);
    setInlineDraft(null);
  };

  const saveInlineEdit = async () => {
    if (!inlineEditId || !inlineDraft) return;
    if (!inlineDraft.venue_name?.trim()) {
      push({ title: "Venue name required", message: "Please provide a venue name." });
      return;
    }
    setInlineSaving(true);
    try {
      const payload = normalizeForm(inlineDraft);
      await updateEntry(inlineEditId, payload);
      push({ title: "Registry updated", message: "Entry updated successfully." });
      cancelInlineEdit();
    } catch {
      push({ title: "Registry update failed", message: "Unable to update registry entry." });
    } finally {
      setInlineSaving(false);
    }
  };

  const handleDelete = async (entry: MasterContactRegistryEntry) => {
    if (!confirm(`Delete registry entry for ${entry.venue_name}?`)) return;
    try {
      await deleteEntry(entry.id);
      push({ title: "Registry entry deleted", message: entry.venue_name });
    } catch (err) {
      push({ title: "Delete failed", message: "Unable to delete registry entry." });
    }
  };

  const handleSearch = async () => {
    await fetchEntries(query.trim() || undefined);
  };

  return (
    <Card className="rounded-2xl border border-border bg-background/60 shadow-none">
      <CardHeader className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <CardTitle className="text-lg font-semibold text-foreground">Master Contact Registry</CardTitle>
          <p className="text-sm text-muted-foreground">
            Owner-managed global venue contact list. Used to short-circuit expensive enrichment.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Input
            placeholder="Search by venue, domain, or email"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            className="h-9 w-56"
          />
          <Button variant="outline" onClick={handleSearch}>
            Search
          </Button>
          <Button variant="ghost" onClick={() => fetchEntries()}>
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="rounded-xl border border-border bg-background/40 p-4">
          <div className="grid gap-3 md:grid-cols-2">
            <Input
              placeholder="Venue name"
              value={form.venue_name ?? ""}
              onChange={(event) => setForm((prev) => ({ ...prev, venue_name: event.target.value }))}
            />
            <Input
              placeholder="Domain (optional)"
              value={form.domain ?? ""}
              onChange={(event) => setForm((prev) => ({ ...prev, domain: event.target.value }))}
            />
            <Input
              placeholder="City"
              value={form.location_city ?? ""}
              onChange={(event) => setForm((prev) => ({ ...prev, location_city: event.target.value }))}
            />
            <Input
              placeholder="Region / State"
              value={form.location_region ?? ""}
              onChange={(event) => setForm((prev) => ({ ...prev, location_region: event.target.value }))}
            />
            <Input
              placeholder="Country"
              value={form.location_country ?? ""}
              onChange={(event) => setForm((prev) => ({ ...prev, location_country: event.target.value }))}
            />
            <Input
              placeholder="Booking email"
              value={form.booking_email ?? ""}
              onChange={(event) => setForm((prev) => ({ ...prev, booking_email: event.target.value }))}
            />
            <Input
              placeholder="Contact URL"
              value={form.contact_url ?? ""}
              onChange={(event) => setForm((prev) => ({ ...prev, contact_url: event.target.value }))}
            />
            <Input
              placeholder="Source URL"
              value={form.source_url ?? ""}
              onChange={(event) => setForm((prev) => ({ ...prev, source_url: event.target.value }))}
            />
          </div>
          <div className="mt-3">
            <Textarea
              placeholder="Notes"
              value={form.notes ?? ""}
              onChange={(event) => setForm((prev) => ({ ...prev, notes: event.target.value }))}
            />
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <Button onClick={handleSubmit} disabled={submitting}>
              {editingId ? "Update entry" : "Add entry"}
            </Button>
            {editingId ? (
              <Button
                variant="ghost"
                onClick={() => {
                  setEditingId(null);
                  setForm(EMPTY_FORM);
                }}
              >
                Cancel
              </Button>
            ) : null}
          </div>
        </div>

        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Venue</TableHead>
                <TableHead>Domain</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Location</TableHead>
                <TableHead>Verified</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {entries.map((entry) => (
                <Fragment key={entry.id}>
                  <TableRow>
                    <TableCell>
                      <div className="text-sm font-semibold text-foreground">{entry.venue_name}</div>
                      <div className="text-xs text-muted-foreground">{entry.normalized_name}</div>
                    </TableCell>
                    <TableCell className="text-sm text-foreground/80">{entry.domain ?? "—"}</TableCell>
                    <TableCell className="text-sm text-foreground/80">{entry.booking_email ?? "—"}</TableCell>
                    <TableCell className="text-sm text-foreground/80">
                      {[entry.location_city, entry.location_region, entry.location_country].filter(Boolean).join(", ") || "—"}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {entry.last_verified_at ? new Date(entry.last_verified_at).toLocaleString() : "—"}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button variant="outline" size="sm" onClick={() => startInlineEdit(entry)}>
                          Inline edit
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => handleEdit(entry)}>
                          Edit form
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(entry)}>
                          Delete
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                  {inlineEditId === entry.id && inlineDraft ? (
                    <TableRow>
                      <TableCell colSpan={6}>
                        <div className="grid gap-3 rounded-xl border border-border bg-background/40 p-3 md:grid-cols-2">
                          <Input
                            placeholder="Venue name"
                            value={inlineDraft.venue_name ?? ""}
                            onChange={(event) =>
                              setInlineDraft((prev) => ({ ...(prev ?? {}), venue_name: event.target.value }))
                            }
                          />
                          <Input
                            placeholder="Domain"
                            value={inlineDraft.domain ?? ""}
                            onChange={(event) =>
                              setInlineDraft((prev) => ({ ...(prev ?? {}), domain: event.target.value }))
                            }
                          />
                          <Input
                            placeholder="Booking email"
                            value={inlineDraft.booking_email ?? ""}
                            onChange={(event) =>
                              setInlineDraft((prev) => ({ ...(prev ?? {}), booking_email: event.target.value }))
                            }
                          />
                          <Input
                            placeholder="Contact URL"
                            value={inlineDraft.contact_url ?? ""}
                            onChange={(event) =>
                              setInlineDraft((prev) => ({ ...(prev ?? {}), contact_url: event.target.value }))
                            }
                          />
                          <Input
                            placeholder="Source URL"
                            value={inlineDraft.source_url ?? ""}
                            onChange={(event) =>
                              setInlineDraft((prev) => ({ ...(prev ?? {}), source_url: event.target.value }))
                            }
                          />
                          <Input
                            placeholder="City"
                            value={inlineDraft.location_city ?? ""}
                            onChange={(event) =>
                              setInlineDraft((prev) => ({ ...(prev ?? {}), location_city: event.target.value }))
                            }
                          />
                          <Input
                            placeholder="Region / State"
                            value={inlineDraft.location_region ?? ""}
                            onChange={(event) =>
                              setInlineDraft((prev) => ({ ...(prev ?? {}), location_region: event.target.value }))
                            }
                          />
                          <Input
                            placeholder="Country"
                            value={inlineDraft.location_country ?? ""}
                            onChange={(event) =>
                              setInlineDraft((prev) => ({ ...(prev ?? {}), location_country: event.target.value }))
                            }
                          />
                          <Textarea
                            placeholder="Notes"
                            value={inlineDraft.notes ?? ""}
                            onChange={(event) =>
                              setInlineDraft((prev) => ({ ...(prev ?? {}), notes: event.target.value }))
                            }
                            className="md:col-span-2"
                          />
                        </div>
                        <div className="mt-3 flex flex-wrap gap-2">
                          <Button onClick={() => void saveInlineEdit()} disabled={inlineSaving}>
                            Save inline
                          </Button>
                          <Button variant="ghost" onClick={cancelInlineEdit}>
                            Cancel
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : null}
                </Fragment>
              ))}
              {entries.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-sm text-muted-foreground">
                    No registry entries found.
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}
